/* ============================================================
   ORION PACKS BUYBOX

   The Daily Protein Packs configurator in the PDP's skin: cases-of-15
   tier tiles, flavour-per-case slots, subscribe/one-time plan
   cards with per-tier discounts.

   A fork of orion-lander-buybox with the DOM re-skinned onto the
   o-buy__* classes the whey PDP uses, so both pages stay visually
   identical from one stylesheet. The purchase model is unchanged:
   each case is its own line item carrying the chosen cadence's
   selling plan, which is how flavour-per-case gets past Shopify's
   per-product option limit. Until the case blocks are wired to
   real products it runs in preview mode: everything works except
   checkout, and the error line says why.
   ============================================================ */
(() => {
  class OrionPacksBuybox extends HTMLElement {
    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.tiers);
        this.flavours = JSON.parse(this.dataset.flavours);
      } catch (e) {
        return;
      }
      this.base = Number(this.dataset.base) || 62;
      this.strings = this.readStrings();
      /* Cold paid traffic is 88% mobile and arrives from an ad, not a
         catalogue. A CTA sitting dead behind three required choices is the
         single most expensive thing this page could do, so every case starts
         pre-filled with the default flavour (the variety pack, unless the
         merchant marks another) and the customer edits only if they care.
         Cadence starts at 45 days: oversupply is the largest cancellation
         reason in the subscription data, and 45 packs / 45 days is exactly
         one a day. */
      const dflt = this.defaultFlavour();
      /* Pick the starting tier by its own flag rather than a fixed index: the
         merchant can drop the smallest tiers, which would otherwise shift the
         index and land the default on the wrong size. */
      const start = Math.max(0, this.tiers.findIndex((t) => t.dflt));
      const size = start >= 0 && this.tiers[start] ? start : 0;
      this.state = {
        size, plan: 0, cadence: this.defaultCadence(),
        cases: Array.from({ length: this.tiers[size].cases }, () => dflt),
      };
      this.els = {
        tiers: this.querySelector('[data-o-tiers]'),
        slots: this.querySelector('[data-o-slots]'),
        options: this.querySelector('[data-o-options]'),
        status: this.querySelector('[data-o-status]'),
        casesHint: this.querySelector('[data-o-cases-hint]'),
        cadence: this.querySelector('[data-o-cadence]'),
        cadencePills: this.querySelector('[data-o-cadence-pills]'),
        rate: this.querySelector('[data-o-rate]'),
        plans: this.querySelector('[data-o-plans]'),
        gifts: this.querySelector('[data-o-gifts]'),
        cta: this.querySelector('[data-o-cta]'),
        alt: this.querySelector('[data-o-alt]'),
        legal: this.querySelector('.o-buy__legal'),
        error: this.querySelector('[data-o-error]'),
      };
      this.els.cta.addEventListener('click', () => this.addToCart());
      if (this.els.alt) this.els.alt.addEventListener('click', () => {
        this.state.plan = this.state.plan === 0 ? 1 : 0;
        this.render();
      });
      this.wireThumbs();
      this.wireNutrition();
      this.wireGiftInfo();
      this.render();
    }

    /* "View nutrition" under the assurance strip. If the page carries an
       orion-nutrition section in embed mode, its whole tabbed UI (flavour
       chooser included) is MOVED into the strip's expander on first open;
       moving keeps the section's own event listeners alive. The old
       single-label modal button hides itself whenever this richer path
       exists. */
    /* ---- selection-matched imagery ----
       Aidan rendered a flat-lay for every flavour combination (complete for
       15/30/45; the four pures plus one-of-each for 60). Files follow
       dpp-<qty>-<sorted letters>.webp, so the current selection maps
       straight to a URL: no manifest to maintain, and any newly uploaded
       render is picked up by name alone. The image crossfades over the
       gallery only when the box is fully configured and an exact render
       exists; anything else falls back to the ordinary gallery. */
    comboLetter(fl) {
      const n = (fl.name || '').toLowerCase();
      if (n.includes('cookie')) return 'c';
      if (n.includes('peanut')) return 'p';
      if (n.includes('cinnamon')) return 'r';
      return 'v';
    }
    updateComboImage() {
      const base = this.dataset.oComboBase;
      const main = this.querySelector('[data-o-main]');
      if (!base || !main) return;
      if (!this.els.comboImg) {
        const im = document.createElement('img');
        im.className = 'o-buy__img o-buy__img--combo';
        im.alt = '';
        im.addEventListener('error', () => {
          /* no render for this combination: fall back to the gallery */
          im.classList.remove('is-showing');
          this.comboUrl = null;
        });
        main.appendChild(im);
        this.els.comboImg = im;
      }
      const im = this.els.comboImg;
      const slots = this.slots;
      const full = slots.length > 0 && slots.every((v) => v !== null && v !== undefined);
      /* the hero keeps the product's own photography until the customer
         actually starts configuring; only then does the box mirror them */
      if (!full || !this.comboEngaged || this.comboManual) {
        im.classList.remove('is-showing');
        this.comboUrl = null;
        return;
      }
      const key = slots.map((v) => this.comboLetter(this.flavours[v])).sort().join('');
      const url = base + 'dpp-' + this.tier.qty + '-' + key + '.webp';
      if (url === this.comboUrl) return;
      this.comboUrl = url;
      const pre = new Image();
      pre.onload = () => {
        if (this.comboUrl !== url) return; /* selection moved on mid-load */
        im.src = url;
        im.classList.add('is-showing');
      };
      pre.onerror = () => {
        if (this.comboUrl !== url) return;
        im.classList.remove('is-showing');
      };
      pre.src = url;
    }

    wireNutrition() {
      /* One nutrition node, two homes. Desktop: the assurance strip's inline
         expander. Mobile: a chip under the ticks opening a bottom sheet. The
         embedded orion-nutrition section is MOVED into whichever home opens
         (moves keep its tab listeners alive), so both surfaces stay in sync
         with the single editor-configured source. */
      const source = document.querySelector('[data-o-nutri-embed-source]');
      if (!source) return;
      const adopt = (slot) => {
        if (source.parentElement !== slot) slot.appendChild(source);
        source.hidden = false;
      };
      const btn = this.querySelector('[data-o-nutri-open]');
      const panel = this.querySelector('[data-o-nutri-panel]');
      if (btn && panel) {
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const opening = !panel.classList.contains('is-open');
          if (opening) {
            adopt(panel.querySelector('[data-o-nutri-slot]'));
            panel.hidden = false;
            /* two frames so the 0fr starting state paints before it grows */
            requestAnimationFrame(() => requestAnimationFrame(() => {
              panel.classList.add('is-open');
            }));
          } else {
            panel.classList.remove('is-open');
          }
          btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        });
      }
      const chip = this.querySelector('[data-o-nutri-chip]');
      const dialog = this.querySelector('[data-o-nutri-dialog]');
      if (chip && dialog) {
        const sheet = dialog.querySelector('.o-buy__media-sheet');
        const openDrawer = () => {
          adopt(dialog.querySelector('[data-o-nutri-dialog-slot]'));
          sheet.scrollTop = 0; /* adopted content keeps scroll between opens */
          dialog.showModal();
          /* keep iOS from scroll-revealing the autofocused element while the
             drawer is still off-screen: take focus without scrolling */
          dialog.focus({ preventScroll: true });
          /* iOS <dialog> does not lock the page behind; without this the
             body rubber-bands under the sheet and the whole open feels off */
          document.documentElement.classList.add('o-drawer-lock');
          /* force the off-screen frame to actually exist before sliding:
             a synchronous layout read beats rAF timing, which WebKit can
             coalesce to before the dialog's first paint */
          dialog.getBoundingClientRect();
          requestAnimationFrame(() => {
            dialog.getBoundingClientRect();
            dialog.classList.add('is-in');
          });
        };
        const closeDrawer = () => {
          dialog.classList.remove('is-in');
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            dialog.style.transform = '';
            dialog.style.transition = '';
            document.documentElement.classList.remove('o-drawer-lock');
            dialog.close();
          };
          dialog.addEventListener('transitionend', done, { once: true });
          setTimeout(done, 460); /* safety: never leave a dead dialog */
        };
        chip.hidden = false;
        chip.addEventListener('click', openDrawer);
        dialog.addEventListener('click', (e) => { if (e.target === dialog) closeDrawer(); });
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeDrawer(); });
        const close = dialog.querySelector('[data-o-nutri-dialog-close]');
        if (close) close.addEventListener('click', closeDrawer);
        /* swipe-down to close: the sheet follows the finger (transition off
           while dragging), then either slides home or slides out. Only arms
           when the sheet is scrolled to its top. */
        let y0 = null;
        sheet.addEventListener('touchstart', (e) => {
          y0 = sheet.scrollTop <= 0 ? e.touches[0].clientY : null;
        }, { passive: true });
        sheet.addEventListener('touchmove', (e) => {
          if (y0 === null) return;
          const dy = e.touches[0].clientY - y0;
          if (dy > 0) {
            dialog.style.transition = 'none';
            dialog.style.transform = 'translateY(' + dy * 0.85 + 'px)';
          }
        }, { passive: true });
        sheet.addEventListener('touchend', (e) => {
          if (y0 === null) return;
          const dy = e.changedTouches[0].clientY - y0;
          dialog.style.transition = '';
          if (dy > 110) {
            closeDrawer();
          } else {
            dialog.style.transform = '';
          }
          y0 = null;
        });
      }
      const fallback = this.querySelector('[data-o-nutri-fallback]');
      if (fallback) fallback.hidden = true;
    }

    wireGiftInfo() {
      /* Same motion contract as the nutrition drawer: transform on the
         dialog, class two frames after showModal, animated exit with a
         settle timeout. Desktop fades and scales; mobile slides up. */
      this.querySelectorAll('[data-o-gift-info]').forEach((btn) => {
        const dialog = this.querySelector('[data-o-gift-dialog="' + btn.getAttribute('data-o-gift-info') + '"]');
        if (!dialog) return;
        const open = () => {
          dialog.showModal();
          dialog.focus({ preventScroll: true });
          document.documentElement.classList.add('o-drawer-lock');
          dialog.getBoundingClientRect();
          requestAnimationFrame(() => {
            dialog.getBoundingClientRect();
            dialog.classList.add('is-in');
          });
        };
        const close = () => {
          dialog.classList.remove('is-in');
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            document.documentElement.classList.remove('o-drawer-lock');
            dialog.close();
          };
          dialog.addEventListener('transitionend', done, { once: true });
          setTimeout(done, 420);
        };
        btn.addEventListener('click', (e) => {
          /* the row lives inside the subscribe plan card; an unstopped click
             selects the plan, re-renders, and rips the dialog out of the
             top layer mid-open */
          e.stopPropagation();
          open();
        });
        dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
        const x = dialog.querySelector('[data-o-gift-close]');
        if (x) x.addEventListener('click', close);
      });
    }

    wireThumbs() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('.o-buy__thumb')) this.comboManual = true;
        else if (e.target.closest('.o-buy__bag-opt, .o-buy__bag, .o-buy__tier')) {
          this.comboManual = false;
          this.comboEngaged = true;
        }
      }, true);
      const main = this.querySelector('[data-o-main] img');
      this.querySelectorAll('[data-o-pack-thumb]').forEach((t) => {
        t.addEventListener('click', () => {
          if (main) { main.src = t.dataset.src; main.removeAttribute('srcset'); }
          this.querySelectorAll('[data-o-pack-thumb]').forEach((x) => x.classList.toggle('is-active', x === t));
        });
      });
    }

    readStrings() {
      const el = this.querySelector('script[data-o-strings]');
      let s = {};
      if (el) {
        try { s = JSON.parse(el.textContent); } catch (e) { /* fall through */ }
      }
      const decodeOne = (v) => {
        if (typeof v !== 'string' || v.indexOf('&') === -1) return v;
        const ta = document.createElement('textarea');
        ta.innerHTML = v;
        return ta.value;
      };
      Object.keys(s).forEach((k) => {
        s[k] = Array.isArray(s[k]) ? s[k].map(decodeOne) : decodeOne(s[k]);
      });
      return Object.assign({
        packs: '{qty} packs', perPack: '{price}/pack', chooseMore: 'Choose {n} more flavour',
        chooseMoreP: 'Choose {n} more flavours', done: '{n} of {n}', subscribeCta: 'Start subscription',
        onceCta: 'Add to cart', subTitle: 'Subscribe & Save', onceTitle: 'One-Time Order', save: 'SAVE {n}%',
        subSub: '{qty} packs every {cad} days · renews at {renew} ({renewOff}% off)',
        onceSub: '{qty} packs, delivered once', subFlag: 'Free shaker + recipe book',
        altOnce: 'Order once for {price} instead', altSub: 'Subscribe and save {n}%',
        hintOne: 'One case of 15 packs.', hintMulti: 'Want {n} of the same? Tap one flavour {n} times.',
        legalSub: '', legalOnce: '',
        preview: 'Wire the flavour case blocks to products in the theme editor to enable checkout.',
        noPlans: 'Subscriptions are not available yet: the case products have no subscription plan for this cadence. One-time ordering still works.',
        addFail: 'Could not add to cart. Please try again.',
        caseLabel: 'Case {n}', caseChoose: 'Choose a flavor',
        cadences: ['30 days', '45 days', '60 days'], subPerks: [], oncePerks: [],
        rateExact: 'Exactly 1 pack a day at this cadence',
        rateMany: 'About {n} packs a day at this cadence',
        rateWeek: 'About {n} packs a week at this cadence',
      }, s);
    }

    fmt(tpl, vars) {
      return String(tpl).replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
    }

    money(n) { return '$' + n.toFixed(2); }

    defaultFlavour() {
      const named = (this.dataset.defaultFlavour || '').trim().toLowerCase();
      if (named) {
        const i = this.flavours.findIndex((f) => (f.name || '').trim().toLowerCase() === named);
        if (i >= 0) return i;
      }
      const variety = this.flavours.findIndex((f) => /variety|mixed|all\s*three|all\s*3/i.test(f.name || ''));
      return variety >= 0 ? variety : 0;
    }

    defaultCadence() {
      const want = parseInt(this.dataset.defaultCadence || '45', 10);
      const i = (this.strings.cadences || []).findIndex((c) => parseInt(String(c).replace(/\D/g, ''), 10) === want);
      return i >= 0 ? i : 0;
    }

    cadenceDays() {
      return parseInt(String(this.strings.cadences[this.state.cadence]).replace(/\D/g, ''), 10);
    }
    /* StayAI hands back a group's plans in whatever order its dashboard holds
       them, and a group may carry cadences this buy box never offers. Reading
       that list by the cadence pill's position would sell the wrong schedule
       in silence: checkout succeeds, the receipt looks right, and only a
       delivery arriving weeks early or late gives it away. Match on the
       interval the plan itself names instead. */
    intervalDays(label) {
      const s = String(label || '').toLowerCase();
      const m = s.match(/(\d+)\s*-?\s*(day|week|month)/);
      if (m) {
        const n = parseInt(m[1], 10);
        if (m[2] === 'week') return n * 7;
        if (m[2] === 'month') return n * 30;
        return n;
      }
      /* A lone number is unambiguous; two or more (a "Save 20%" in the name)
         are not, so refuse rather than guess. */
      const nums = s.match(/\d+/g);
      return nums && nums.length === 1 ? parseInt(nums[0], 10) : 0;
    }
    planFor(f) {
      const want = this.cadenceDays();
      /* A plan is only good enough if it bills what the page promises: its
         interval must match the chosen cadence AND its percentage must match
         this tier's advertised discount. StayAI pricing is edited in their
         dashboard, out of this theme's sight, so the page re-checks instead
         of trusting; a drifted percentage disables subscribe rather than
         silently charging something the tile never said. */
      const tierOff = this.tier ? this.tier.subOff : null;
      if (f.planList && f.planList.length) {
        const hit = f.planList.find((p) =>
          this.intervalDays(p.label) === want &&
          (p.pct === undefined || p.pct === null || p.pct === tierOff)
        );
        return hit ? hit.id : 0;
      }
      /* Markup cached from before planList shipped carried ids positionally. */
      return (f.plans && f.plans[this.state.cadence]) || 0;
    }
    /* Packs per day at the chosen tier and cadence. Oversupply outranks price
       as a cancellation reason, so the page states the consumption rate rather
       than leaving the customer to work it out after the second delivery. */
    rateLine() {
      const days = this.cadenceDays();
      if (!days) return '';
      const perDay = this.tier.qty / days;
      const tpl = this.strings.rateExact || 'Exactly 1 pack a day at this cadence';
      if (Math.abs(perDay - 1) < 0.05) return tpl;
      if (perDay >= 1) return this.fmt(this.strings.rateMany || 'About {n} packs a day at this cadence', { n: Math.round(perDay * 10) / 10 });
      return this.fmt(this.strings.rateWeek || 'About {n} packs a week at this cadence', { n: Math.round(perDay * 7) });
    }

    get tier() { return this.tiers[this.state.size]; }

    get slots() {
      const n = this.tier.cases;
      const cs = this.state.cases;
      return Array.from({ length: n }, (_, i) => (cs[i] !== undefined ? cs[i] : null));
    }

    prices() {
      const t = this.tier;
      const list = this.base * t.cases;
      return {
        list,
        once: list * (1 - t.onceOff / 100),
        sub: list * (1 - t.subOff / 100),
        renew: list * (1 - t.renewOff / 100),
      };
    }

    render() {
      const s = this.state;
      const t = this.tier;
      const p = this.prices();
      const slots = this.slots;
      const filled = slots.filter((v) => v !== null).length;
      const gated = filled < t.cases;
      const str = this.strings;

      /* ---- tier tiles (o-buy__tier, as on the whey PDP) ---- */
      this.els.tiers.replaceChildren(...this.tiers.map((tt, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-buy__tier' + (i === s.size ? ' is-on' : '');
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', i === s.size ? 'true' : 'false');
        if (tt.tag) {
          const tag = document.createElement('span');
          tag.className = 'o-buy__tier-tag'; tag.textContent = tt.tag;
          b.appendChild(tag);
        }
        const qty = document.createElement('span');
        qty.className = 'o-buy__tier-qty'; qty.textContent = tt.qty;
        b.appendChild(qty);
        const unit = document.createElement('span');
        unit.className = 'o-buy__tier-unit';
        unit.textContent = this.fmt(str.packs, { qty: '' }).trim() || 'packs';
        b.appendChild(unit);
        const per = document.createElement('span');
        per.className = 'o-buy__tier-per';
        /* the tile quotes whichever plan is selected: subscribe shows the
           tier's subscription per-pack, one-time shows the one-time per-pack */
        const off = s.plan === 0 ? tt.subOff : tt.onceOff;
        const sp = this.base * tt.cases * (1 - off / 100);
        per.textContent = this.fmt(str.perPack, { price: this.money(sp / tt.qty) });
        b.appendChild(per);
        b.addEventListener('click', () => {
          s.size = i;
          /* Growing a tier used to leave the new case empty, which disabled the
             CTA at exactly the moment the customer chose a bigger box. New
             slots inherit the default flavour; existing choices are kept. */
          const dflt = this.defaultFlavour();
          const next = this.slots.slice(0, tt.cases);
          for (let n = 0; n < tt.cases; n += 1) {
            if (next[n] === null || next[n] === undefined) next[n] = dflt;
          }
          s.cases = next;
          this.render();
        });
        return b;
      }));

      /* ---- case slots (o-buy__bag, as the 3-month builder) ---- */
      this.els.slots.replaceChildren(...slots.map((v, i) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'o-buy__bag' + (v !== null ? ' is-filled' : '');
        const lab = document.createElement('span');
        lab.className = 'o-buy__bag-label';
        lab.textContent = this.fmt(str.caseLabel, { n: i + 1 });
        el.appendChild(lab);
        if (v !== null) {
          const fl = this.flavours[v];
          if (fl.img) {
            const im = document.createElement('img');
            im.src = fl.img; im.alt = ''; im.loading = 'lazy';
            el.appendChild(im);
          }
          const nm = document.createElement('span');
          nm.className = 'o-buy__bag-name'; nm.textContent = fl.short;
          el.appendChild(nm);
          const x = document.createElement('span');
          x.className = 'o-buy__bag-x'; x.textContent = '×';
          x.setAttribute('aria-hidden', 'true');
          el.appendChild(x);
          el.addEventListener('click', () => { s.cases[i] = null; this.render(); });
        } else {
          const plus = document.createElement('span');
          plus.className = 'o-buy__bag-plus'; plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          el.appendChild(plus);
          const nm = document.createElement('span');
          nm.className = 'o-buy__bag-name is-empty'; nm.textContent = str.caseChoose;
          el.appendChild(nm);
        }
        return el;
      }));

      /* ---- flavour options (o-buy__bag-opt rows) ---- */
      this.els.options.replaceChildren(...this.flavours.map((fl, fi) => {
        const count = slots.filter((v) => v === fi).length;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-buy__bag-opt' + (count ? ' is-on' : '');
        if (fl.img) {
          const im = document.createElement('img');
          im.src = fl.img; im.alt = ''; im.loading = 'lazy';
          b.appendChild(im);
        }
        b.appendChild(document.createTextNode(fl.short));
        const tally = document.createElement('span');
        tally.className = 'o-buy__bag-tally';
        tally.textContent = count ? '×' + count : '+';
        b.appendChild(tally);
        b.addEventListener('click', () => {
          /* Tapping a flavour always lands it in a case. Empty slots fill
             first; when the box is full the tap replaces cases in rotation,
             so switching from the default takes one tap, not remove-then-add.
             Skips cases already holding this flavour. */
          let idx = this.slots.indexOf(null);
          if (idx === -1) {
            const n = s.cases.length;
            const start = this.fillPtr || 0;
            for (let step = 0; step < n; step += 1) {
              const j = (start + step) % n;
              if (s.cases[j] !== fi) { idx = j; break; }
            }
          }
          if (idx === -1) return; /* every case is already this flavour */
          s.cases[idx] = fi;
          this.fillPtr = (idx + 1) % s.cases.length;
          this.render();
        });
        return b;
      }));

      /* ---- status + hint ---- */
      if (this.els.status) {
        this.els.status.textContent = gated
          ? this.fmt(t.cases - filled === 1 ? str.chooseMore : str.chooseMoreP, { n: t.cases - filled })
          : '';
      }
      this.els.casesHint.textContent = t.cases === 1 ? str.hintOne : this.fmt(str.hintMulti, { n: t.cases });

      /* ---- cadence pills (nested into the subscribe card below) ---- */
      this.els.cadencePills.replaceChildren(...str.cadences.map((label, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-buy__pill' + (i === s.cadence ? ' is-on' : '');
        b.textContent = label;
        b.addEventListener('click', (e) => { e.stopPropagation(); s.cadence = i; this.render(); });
        return b;
      }));
      if (this.els.rate) {
        this.els.rate.textContent = this.rateLine();
        this.els.rate.hidden = s.plan !== 0 || !this.els.rate.textContent;
      }
      this.els.cadence.hidden = s.plan !== 0;

      /* ---- plan cards (o-buy__plan with ribbon, dot, price column) ---- */
      const cad = String(str.cadences[s.cadence]).replace(/\D+$/, '').trim() || str.cadences[s.cadence];
      const plans = [
        {
          title: str.subTitle,
          save: this.fmt(str.save, { n: t.subOff }),
          price: p.sub, was: p.list,
          per: this.fmt(str.perPack, { price: this.money(p.sub / t.qty) }),
          sub: this.fmt(str.subSub, { qty: t.qty, cad: cad, renew: this.money(p.renew), renewOff: t.renewOff }),
          flag: str.subFlag, perks: str.subPerks, total: p.sub, cta: str.subscribeCta,
        },
        {
          title: str.onceTitle,
          save: t.onceOff ? this.fmt(str.save, { n: t.onceOff }) : '',
          price: p.once, was: t.onceOff ? p.list : 0,
          per: this.fmt(str.perPack, { price: this.money(p.once / t.qty) }),
          sub: this.fmt(str.onceSub, { qty: t.qty }),
          flag: '', perks: str.oncePerks, total: p.once, cta: str.onceCta,
        },
      ];
      this.els.plans.replaceChildren(...plans.map((pl, i) => {
        const el = document.createElement('div');
        el.className = 'o-buy__plan' + (i === s.plan ? ' is-on' : '');
        el.setAttribute('role', 'radio');
        el.setAttribute('aria-checked', i === s.plan ? 'true' : 'false');
        el.tabIndex = 0;
        if (pl.flag) {
          const rb = document.createElement('span');
          rb.className = 'o-buy__plan-ribbon'; rb.textContent = pl.flag;
          el.appendChild(rb);
          el.classList.add('has-ribbon');
        }
        const head = document.createElement('div');
        head.className = 'o-buy__plan-head';
        const dot = document.createElement('span');
        dot.className = 'o-buy__plan-dot';
        dot.setAttribute('aria-hidden', 'true');
        head.appendChild(dot);
        const title = document.createElement('span');
        title.className = 'o-buy__plan-title'; title.textContent = pl.title;
        head.appendChild(title);
        if (pl.save) {
          const sv = document.createElement('span');
          sv.className = 'o-buy__plan-save'; sv.textContent = pl.save;
          head.appendChild(sv);
        }
        const priceCol = document.createElement('span');
        priceCol.className = 'o-buy__plan-pricecol';
        const pr = document.createElement('span');
        pr.className = 'o-buy__plan-price';
        pr.textContent = this.money(pl.price) + ' ';
        if (pl.was) {
          const w = document.createElement('s');
          w.className = 'o-buy__plan-was'; w.textContent = this.money(pl.was);
          pr.appendChild(w);
        }
        priceCol.appendChild(pr);
        const per = document.createElement('span');
        per.className = 'o-buy__plan-per'; per.textContent = pl.per;
        priceCol.appendChild(per);
        head.appendChild(priceCol);
        el.appendChild(head);
        /* Only the selected card carries its detail. An unselected card
           collapses to title, price and radio, so choosing one-time visibly
           puts the subscribe card away instead of leaving two open panels
           competing for the same decision. */
        if (i === s.plan) {
          const sub = document.createElement('p');
          sub.className = 'o-buy__plan-sub'; sub.textContent = pl.sub;
          el.appendChild(sub);
        }
        if (i === s.plan && pl.perks && pl.perks.length) {
          const ul = document.createElement('ul');
          ul.className = 'o-buy__plan-perks';
          pl.perks.forEach((perk) => {
            const li = document.createElement('li');
            li.textContent = String(perk).replace('{renewOff}', t.renewOff);
            ul.appendChild(li);
          });
          el.appendChild(ul);
        }
        if (i === s.plan && i === 0) {
          if (this.els.cadence) el.appendChild(this.els.cadence);
          /* The rate line must travel WITH the cadence pills. Left as a
             sibling in the rail it keeps its source position, and on mobile
             the flattened flex column gives any element without an explicit
             `order` the default 0, which throws it above the product title. */
          if (this.els.rate) el.appendChild(this.els.rate);
          if (this.els.gifts) el.appendChild(this.els.gifts);
        }
        const pick = () => {
          const changed = s.plan !== i;
          s.plan = i;
          this.render();
          /* Switching plan re-opens the flavour question: the cases are what
             the customer is actually buying, and on mobile the plan cards sit
             well below them. Scroll back rather than leaving them to find it. */
          if (changed && this.els.slots) {
            const anchor = this.els.slots.closest('.o-buy__step') || this.els.slots;
            anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
            anchor.classList.add('is-flash');
            setTimeout(() => anchor.classList.remove('is-flash'), 900);
          }
        };
        el.addEventListener('click', pick);
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
        });
        return el;
      }));

      if (this.els.gifts) this.els.gifts.hidden = s.plan !== 0;

      /* ---- CTA ----
         Two separate readiness questions, and conflating them is dangerous.

         `wired` asks whether the case products resolve to buyable variants.
         `planned` asks whether those variants carry a selling plan for the
         chosen cadence. Checking only the first would let someone press
         "Start subscription", be charged the subscription price, and receive a
         ONE-TIME order, because addToCart simply omits selling_plan when none
         exists. Selling plans are created in StayAI's dashboard, not by this
         theme, so this state is reachable the moment the products go live and
         before the plans are attached. */
      const wired = this.flavours.length > 0 && this.flavours.every((f) => f.variant);
      const subscribing = s.plan === 0;
      const planned = !subscribing || this.flavours.every((f) => this.planFor(f));
      const ready = wired && planned;
      const active = plans[s.plan];
      if (gated) {
        this.els.cta.disabled = true;
        this.els.cta.textContent = this.fmt(
          t.cases - filled === 1 ? str.chooseMore : str.chooseMoreP,
          { n: t.cases - filled }
        );
      } else {
        this.els.cta.disabled = !ready;
        this.els.cta.textContent = active.cta + ' · ' + this.money(active.total);
      }
      this.els.error.hidden = true;
      if (!gated && !ready) {
        this.els.error.textContent = wired ? str.noPlans : str.preview;
        this.els.error.hidden = false;
      }
      this.updateComboImage();
      if (this.els.alt) this.els.alt.textContent = s.plan === 0
        ? this.fmt(str.altOnce, { price: this.money(p.once) })
        : this.fmt(str.altSub, { n: t.subOff });
      if (this.els.legal && (str.legalSub || str.legalOnce)) {
        this.els.legal.textContent = s.plan === 0
          ? this.fmt(str.legalSub, { cad: cad, subOff: t.subOff, renewOff: t.renewOff })
          : str.legalOnce;
      }
    }

    async addToCart() {
      const slots = this.slots;
      if (slots.some((v) => v === null)) return;
      const subscribe = this.state.plan === 0;
      const items = [];
      for (const v of slots) {
        const fl = this.flavours[v];
        if (!fl.variant) return;
        /* Never sell a subscription as a one-off. If the plan is missing the
           order would look identical to the customer and bill once. */
        const plan = subscribe ? this.planFor(fl) : 0;
        if (subscribe && !plan) {
          this.els.error.textContent = this.strings.noPlans;
          this.els.error.hidden = false;
          return;
        }
        const item = { id: fl.variant, quantity: 1 };
        if (plan) item.selling_plan = plan;
        items.push(item);
      }
      this.els.cta.classList.add('is-loading');
      this.els.error.hidden = true;
      try {
        const sectionIds = [...document.querySelectorAll('cart-items-component')]
          .map((el) => el.dataset.sectionId).filter(Boolean);
        const body = { items };
        if (sectionIds.length) body.sections = sectionIds.join(',');
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const resp = await r.json();
        if (!r.ok) throw new Error(resp.description || this.strings.addFail);
        document.dispatchEvent(new CustomEvent('orion:cart:added', { detail: { sections: resp.sections } }));
      } catch (err) {
        this.els.error.textContent = err.message || this.strings.addFail;
        this.els.error.hidden = false;
      } finally {
        this.els.cta.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('orion-packs-buybox')) {
    customElements.define('orion-packs-buybox', OrionPacksBuybox);
  }
})();
